import type { CyberThreat, CyberAttackType, ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// Cyber Threat Intelligence — Recorded Future style
// Real-world OSINT-based threat actor campaigns
// ---------------------------------------------------------------------------

interface ThreatActorProfile {
  name: string;
  country: string;
  originLon: number;
  originLat: number;
  attribution: string;   // e.g. 'North Korean state-sponsored'
  specialties: CyberAttackType[];
}

const THREAT_ACTORS: ThreatActorProfile[] = [
  { name: 'Lazarus Group',    country: 'North Korea', originLon: 127.0, originLat: 39.0, attribution: 'DPRK State-Sponsored (RGB)', specialties: ['apt', 'ransomware', 'data-breach'] },
  { name: 'Fancy Bear (APT28)', country: 'Russia', originLon: 37.6, originLat: 55.8, attribution: 'GRU Russian Military Intel', specialties: ['espionage', 'disinformation', 'apt'] },
  { name: 'Sandworm',         country: 'Russia', originLon: 30.5, originLat: 59.9, attribution: 'GRU Unit 74455', specialties: ['infrastructure', 'apt', 'ransomware'] },
  { name: 'APT41',            country: 'China', originLon: 116.4, originLat: 39.9, attribution: 'MSS / PLA affiliated', specialties: ['espionage', 'supply-chain', 'apt'] },
  { name: 'Volt Typhoon',     country: 'China', originLon: 121.5, originLat: 31.2, attribution: 'PLA-affiliated (pre-positioning)', specialties: ['infrastructure', 'apt'] },
  { name: 'Salt Typhoon',     country: 'China', originLon: 113.3, originLat: 23.1, attribution: 'MSS Telecom Operations', specialties: ['espionage', 'apt', 'data-breach'] },
  { name: 'Charming Kitten',  country: 'Iran', originLon: 51.4, originLat: 35.7, attribution: 'IRGC Intelligence', specialties: ['espionage', 'apt', 'disinformation'] },
  { name: 'Kimsuky',          country: 'North Korea', originLon: 125.7, originLat: 39.0, attribution: 'DPRK RGB Recon Bureau', specialties: ['espionage', 'apt'] },
  { name: 'LockBit RaaS',     country: 'Russia', originLon: 60.0, originLat: 56.0, attribution: 'Cybercriminal (RU-based)', specialties: ['ransomware', 'data-breach'] },
  { name: 'BlackCat (ALPHV)', country: 'Russia', originLon: 44.0, originLat: 56.0, attribution: 'Ransomware-as-a-Service', specialties: ['ransomware', 'infrastructure'] },
];

export const CYBER_THREATS: CyberThreat[] = [
  // Lazarus Group → Global Finance
  {
    id: 'ct-lzr-us-fin',
    name: 'Operation AppleJeus 2026',
    type: 'apt',
    threatActor: 'Lazarus Group',
    originCountry: 'North Korea',
    originLon: 127.0, originLat: 39.0,
    targetCountry: 'United States',
    targetSector: 'Finance & Crypto',
    targetLon: -73.9, targetLat: 40.7,
    severity: 'critical',
    timestamp: '2026-03-01T00:00:00Z',
    description: 'Lazarus Group targeting cryptocurrency exchanges and DeFi protocols via trojanized trading software. $1.2B in crypto assets at risk.',
    affectedSectors: ['Finance', 'Cryptocurrency', 'Banking'],
    iocs: ['185.234.x.x', 'malware-dropper.exe', 'apt-beacon.dll'],
    cveIds: ['CVE-2025-44234', 'CVE-2025-38901'],
    active: true,
  },
  // Sandworm → Ukraine Energy
  {
    id: 'ct-sw-ua-energy',
    name: 'FrostyGoop Power Grid Attack',
    type: 'infrastructure',
    threatActor: 'Sandworm',
    originCountry: 'Russia',
    originLon: 37.6, originLat: 55.8,
    targetCountry: 'Ukraine',
    targetSector: 'Energy Grid',
    targetLon: 30.5, targetLat: 50.4,
    severity: 'critical',
    timestamp: '2026-02-28T00:00:00Z',
    description: 'Sandworm deploying FrostyGoop ICS malware against Ukrainian power substations. 600,000+ civilians affected by outages.',
    affectedSectors: ['Energy', 'Critical Infrastructure', 'Water'],
    iocs: ['frostygoop.exe', '91.200.x.x', 'ics-beacon.dll'],
    cveIds: ['CVE-2026-0127'],
    active: true,
  },
  // Volt Typhoon → US Infrastructure (pre-positioning)
  {
    id: 'ct-vt-us-infra',
    name: 'Volt Typhoon — Living off the Land',
    type: 'infrastructure',
    threatActor: 'Volt Typhoon',
    originCountry: 'China',
    originLon: 121.5, originLat: 31.2,
    targetCountry: 'United States',
    targetSector: 'Critical Infrastructure',
    targetLon: -98.0, targetLat: 38.0,
    severity: 'critical',
    timestamp: '2026-01-15T00:00:00Z',
    description: 'PLA-affiliated Volt Typhoon pre-positioning in US water, power, and port infrastructure. CISA issued emergency directive.',
    affectedSectors: ['Water', 'Power', 'Ports', 'Telecommunications'],
    iocs: ['living-off-the-land', 'certutil', 'wmic'],
    active: true,
  },
  // Salt Typhoon → Telecom espionage
  {
    id: 'ct-st-us-telecom',
    name: 'Salt Typhoon Telecom Breach',
    type: 'espionage',
    threatActor: 'Salt Typhoon',
    originCountry: 'China',
    originLon: 113.3, originLat: 23.1,
    targetCountry: 'United States',
    targetSector: 'Telecommunications',
    targetLon: -95.0, targetLat: 37.0,
    severity: 'critical',
    timestamp: '2025-10-01T00:00:00Z',
    description: 'Chinese MSS actors compromised AT&T, Verizon, Lumen. Intercepted communications of senior US officials and political campaigns.',
    affectedSectors: ['Telecommunications', 'Government', 'Intelligence'],
    iocs: ['cisco-ios-backdoor', '2401:db00:x:x:x::1'],
    active: true,
  },
  // APT41 → Supply Chain
  {
    id: 'ct-a41-eu-supply',
    name: 'APT41 Software Supply Chain',
    type: 'supply-chain',
    threatActor: 'APT41',
    originCountry: 'China',
    originLon: 116.4, originLat: 39.9,
    targetCountry: 'Germany',
    targetSector: 'Defense Manufacturing',
    targetLon: 13.4, targetLat: 52.5,
    severity: 'high',
    timestamp: '2026-02-10T00:00:00Z',
    description: 'APT41 compromised German defense contractor software supply chain. Intellectual property theft targeting next-gen fighter program.',
    affectedSectors: ['Defense', 'Aerospace', 'Manufacturing'],
    iocs: ['malicious-update-pkg.zip', '103.79.x.x'],
    cveIds: ['CVE-2025-48822'],
    active: true,
  },
  // Charming Kitten → Israel / UAE
  {
    id: 'ct-ck-il',
    name: 'Charming Kitten — Spear Phishing',
    type: 'espionage',
    threatActor: 'Charming Kitten',
    originCountry: 'Iran',
    originLon: 51.4, originLat: 35.7,
    targetCountry: 'Israel',
    targetSector: 'Government & Military',
    targetLon: 34.8, targetLat: 32.0,
    severity: 'high',
    timestamp: '2026-03-05T00:00:00Z',
    description: 'IRGC-linked Charming Kitten targeting Israeli defense officials with spear-phishing, credential harvesting via fake conference invites.',
    affectedSectors: ['Government', 'Military', 'Research'],
    iocs: ['conf-invitation.pdf', '194.165.x.x'],
    active: true,
  },
  // LockBit → Hospitals
  {
    id: 'ct-lb-eu-health',
    name: 'LockBit Healthcare Ransomware',
    type: 'ransomware',
    threatActor: 'LockBit RaaS',
    originCountry: 'Russia',
    originLon: 60.0, originLat: 56.0,
    targetCountry: 'United Kingdom',
    targetSector: 'Healthcare (NHS)',
    targetLon: -0.1, targetLat: 51.5,
    severity: 'high',
    timestamp: '2026-02-20T00:00:00Z',
    description: 'LockBit affiliate encrypts NHS hospital records. 47 hospitals affected, emergency services diverted. $80M ransom demanded.',
    affectedSectors: ['Healthcare', 'Emergency Services'],
    iocs: ['lockbit4.exe', '192.168.x.x', 'ransom-note.txt'],
    active: true,
  },
  // Fancy Bear → NATO
  {
    id: 'ct-fb-nato',
    name: 'Fancy Bear NATO Espionage Campaign',
    type: 'espionage',
    threatActor: 'Fancy Bear (APT28)',
    originCountry: 'Russia',
    originLon: 37.6, originLat: 55.8,
    targetCountry: 'Belgium',
    targetSector: 'NATO Headquarters',
    targetLon: 4.4, targetLat: 50.9,
    severity: 'critical',
    timestamp: '2026-03-01T00:00:00Z',
    description: 'GRU APT28 compromising NATO member state foreign ministries via zero-day in VPN appliances. Targeting Ukraine war planning documents.',
    affectedSectors: ['Government', 'Military', 'Diplomatic'],
    iocs: ['zerologon-variant', '185.220.x.x'],
    cveIds: ['CVE-2026-1337'],
    active: true,
  },
  // Kimsuky → South Korea
  {
    id: 'ct-ks-kr',
    name: 'Kimsuky Korean Defense Theft',
    type: 'apt',
    threatActor: 'Kimsuky',
    originCountry: 'North Korea',
    originLon: 125.7, originLat: 39.0,
    targetCountry: 'South Korea',
    targetSector: 'Defense & Think Tanks',
    targetLon: 126.9, targetLat: 37.5,
    severity: 'high',
    timestamp: '2026-02-25T00:00:00Z',
    description: 'Kimsuky targeting Korean defense ministry officials and RAND Korea analysts. Stealing F-35 maintenance manuals and THAAD operational data.',
    affectedSectors: ['Defense', 'Government', 'Research'],
    iocs: ['hancitor.dll', '175.45.x.x'],
    active: true,
  },
  // BlackCat → Energy sector
  {
    id: 'ct-bc-us-energy',
    name: 'BlackCat Energy Sector Attack',
    type: 'ransomware',
    threatActor: 'BlackCat (ALPHV)',
    originCountry: 'Russia',
    originLon: 44.0, originLat: 56.0,
    targetCountry: 'United States',
    targetSector: 'Oil & Gas',
    targetLon: -95.4, targetLat: 29.8,
    severity: 'high',
    timestamp: '2026-03-03T00:00:00Z',
    description: 'BlackCat ALPHV targeting US Gulf Coast refineries with triple-extortion ransomware. Pipeline operations disrupted, fuel prices spiking.',
    affectedSectors: ['Energy', 'Oil & Gas', 'Pipeline'],
    iocs: ['alphv-encryptor.exe', '185.220.x.x'],
    active: false, // Resolved
  },
];

export function fetchCyberThreats(): CyberThreat[] {
  return CYBER_THREATS;
}

export function getCyberThreatsByActor(actor: string): CyberThreat[] {
  return CYBER_THREATS.filter((t) => t.threatActor === actor);
}

export function getActiveCyberThreats(): CyberThreat[] {
  return CYBER_THREATS.filter((t) => t.active);
}

// Threat severity color (for UI)
export const CYBER_SEVERITY_COLORS: Record<ThreatLevel, string> = {
  critical: '#ef4444',
  high:     '#f97316',
  medium:   '#eab308',
  low:      '#22c55e',
  info:     '#64748b',
};

// Attack type display
export const ATTACK_TYPE_LABELS: Record<CyberAttackType, string> = {
  'ransomware':     '💰 Ransomware',
  'ddos':           '🌊 DDoS',
  'apt':            '🕵️ APT',
  'data-breach':    '🗂️ Data Breach',
  'infrastructure': '⚡ ICS/Infrastructure',
  'espionage':      '👁️ Espionage',
  'disinformation': '📢 Disinfo',
  'supply-chain':   '🔗 Supply Chain',
};

export { THREAT_ACTORS };
